import { useId } from 'react'

import { Input, Select } from 'antd'

import { ErrorLabel } from 'common/components/ErrorLabel/ErrorLabel'
import { Label } from 'common/components/Label/Label'
import { SeriesCheckbox } from 'features/Schedule/pages/components/ManageAppointment/EhrAppointmentV2/Series/controls/SeriesCheckbox'
import { SeriesStepper } from 'features/Schedule/pages/components/ManageAppointment/EhrAppointmentV2/Series/controls/SeriesStepper'
import { UnitSegmented } from 'features/Schedule/pages/components/ManageAppointment/EhrAppointmentV2/Series/controls/UnitSegmented'
import { SERIES_PAGES_TEXT } from 'features/Series/pages/shared/seriesPagesText'

import { snapDuration, type SeriesTemplateDraft, type TemplateField } from '../templateForm.model'
import { TemplateBillingField } from './TemplateBillingField'
import { TemplateIssuesField } from './TemplateIssuesField'
import styles from './templateModal.module.scss'

const TEXT = SERIES_PAGES_TEXT.modal
const NAME_MAX = 150
const DESCRIPTION_MAX = 1000
const VISITS = { min: 1, max: 104 }
const EVERY = { min: 1, max: 999 }
// 995 is the last multiple of 5 under the contract's 999.
const DURATION = { min: 5, max: 995, step: 5 }
const DESCRIPTION_ROWS = { minRows: 2, maxRows: 4 }

export const NO_CATEGORY = 'none'
export const ANY_FACILITY = 'any'
export const SITE_FACILITY = 'site'

export type CategoryValue = number | typeof NO_CATEGORY
export type FacilityValue = typeof ANY_FACILITY | typeof SITE_FACILITY
export type SelectOption<V> = { value: V; label: string }

type Props = {
  draft: SeriesTemplateDraft
  onChange: (patch: Partial<SeriesTemplateDraft>) => void
  errors: Partial<Record<TemplateField, string>>
  disabled: boolean
  siteId: string
  categoryOptions: SelectOption<CategoryValue>[]
  categoriesLoading: boolean
  facilityOptions: SelectOption<FacilityValue>[]
}

// The first error among the fields shown together in one row.
const firstError = (
  errors: Partial<Record<TemplateField, string>>,
  keys: TemplateField[],
): string | undefined => keys.map((key) => errors[key]).find(Boolean)

export const TemplateFields = ({
  draft,
  onChange,
  errors,
  disabled,
  siteId,
  categoryOptions,
  categoriesLoading,
  facilityOptions,
}: Props) => {
  const ids = useId()

  return (
    <div className={styles.body}>
      <div className={styles.frow} data-testid='template-row-name'>
        <Label id={`${ids}-name`} label={TEXT.name} required className={styles.flbl} />
        <div className={styles.cell}>
          <Input
            id={`${ids}-name`}
            className={styles.input}
            value={draft.name}
            maxLength={NAME_MAX}
            placeholder={TEXT.namePlaceholder}
            disabled={disabled}
            status={errors.name ? 'error' : undefined}
            onChange={(e) => onChange({ name: e.target.value })}
          />
          <ErrorLabel error={errors.name} />
        </div>
      </div>

      <div className={styles.frow}>
        <Label id={`${ids}-description`} label={TEXT.description} className={styles.flbl} />
        <div className={styles.cell}>
          <Input.TextArea
            id={`${ids}-description`}
            className={styles.textarea}
            value={draft.description}
            maxLength={DESCRIPTION_MAX}
            autoSize={DESCRIPTION_ROWS}
            disabled={disabled}
            status={errors.description ? 'error' : undefined}
            onChange={(e) => onChange({ description: e.target.value })}
          />
          <ErrorLabel error={errors.description} />
        </div>
      </div>

      <div className={styles.frow}>
        <Label label={TEXT.visits} className={styles.flbl} />
        <div className={styles.cell}>
          <div className={styles.cadenceRow}>
            <div>
              <SeriesStepper
                value={draft.visits}
                {...VISITS}
                editable
                inputLabel={TEXT.visits}
                decLabel={TEXT.fewerVisits}
                incLabel={TEXT.moreVisits}
                disabled={disabled}
                onChange={(visits) => onChange({ visits })}
              />
              <div className={styles.hint}>{TEXT.visitsHint}</div>
            </div>
            <div>
              <div className={styles.inline}>
                <span className={styles.flbl}>{TEXT.cadence}</span>
                <span className={styles.muted}>{TEXT.every}</span>
                <SeriesStepper
                  value={draft.every}
                  {...EVERY}
                  editable
                  inputLabel={TEXT.every}
                  decLabel={TEXT.lessOften}
                  incLabel={TEXT.moreOften}
                  disabled={disabled}
                  onChange={(every) => onChange({ every })}
                />
                <UnitSegmented
                  value={draft.unit}
                  ariaLabel={TEXT.cadence}
                  disabled={disabled}
                  onChange={(unit) => onChange({ unit })}
                />
              </div>
              <div className={`${styles.hint} ${styles.cadenceHint}`}>{TEXT.cadenceHint}</div>
            </div>
            <div>
              <div className={styles.inline}>
                <span className={styles.flbl}>{TEXT.visitLength}</span>
                <SeriesStepper
                  value={draft.duration}
                  {...DURATION}
                  editable
                  normalize={snapDuration}
                  inputLabel={TEXT.visitLength}
                  decLabel={TEXT.shorter}
                  incLabel={TEXT.longer}
                  disabled={disabled}
                  onChange={(duration) => onChange({ duration })}
                />
                <span className={styles.muted}>{TEXT.minutes}</span>
              </div>
              <div className={`${styles.hint} ${styles.lengthHint}`}>{TEXT.visitLengthHint}</div>
            </div>
          </div>
          <ErrorLabel error={firstError(errors, ['visits', 'every', 'unit', 'duration'])} />
        </div>
      </div>

      <div className={styles.frow}>
        <Label id={`${ids}-category`} label={TEXT.category} className={styles.flbl} />
        <div className={styles.categoryRow}>
          <div className={styles.category}>
            <Select<CategoryValue>
              id={`${ids}-category`}
              className={styles.select}
              value={draft.categoryId ?? NO_CATEGORY}
              options={categoryOptions}
              loading={categoriesLoading}
              disabled={disabled}
              status={errors.category_id ? 'error' : undefined}
              onChange={(value) => onChange({ categoryId: value === NO_CATEGORY ? null : value })}
            />
            <div className={styles.hint}>{TEXT.categoryHint}</div>
            <ErrorLabel error={errors.category_id} />
          </div>
          <div className={styles.billing}>
            <span className={styles.flbl}>{TEXT.billingCode}</span>
            <div className={styles.cell}>
              <TemplateBillingField
                value={draft.billingCode}
                disabled={disabled}
                error={errors.billing_code}
                onChange={(billingCode) => onChange({ billingCode })}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={styles.frow} data-testid='template-row-issues'>
        <Label label={TEXT.medicalIssue} className={styles.flbl} />
        <div className={styles.cell}>
          <TemplateIssuesField
            value={draft.issues}
            siteId={siteId}
            disabled={disabled}
            error={errors.issue_codes}
            onChange={(issues) => onChange({ issues })}
          />
        </div>
      </div>

      <div className={styles.frow}>
        <Label id={`${ids}-facility`} label={TEXT.facility} className={styles.flbl} />
        <div className={styles.cell}>
          <Select<FacilityValue>
            id={`${ids}-facility`}
            className={styles.select}
            value={draft.anyFacility ? ANY_FACILITY : SITE_FACILITY}
            options={facilityOptions}
            disabled={disabled}
            status={errors.any_facility ? 'error' : undefined}
            onChange={(value) => onChange({ anyFacility: value === ANY_FACILITY })}
          />
          <ErrorLabel error={errors.any_facility} />
        </div>
      </div>

      <div className={styles.frow}>
        <Label label={TEXT.available} className={`${styles.flbl} ${styles.flblCheck}`} />
        <div className={styles.checkRow}>
          <SeriesCheckbox
            checked={draft.active}
            disabled={disabled}
            onChange={(active) => onChange({ active })}>
            {TEXT.availableCheckbox}
          </SeriesCheckbox>
        </div>
      </div>
    </div>
  )
}
